import { useEffect, useState, useCallback } from 'react';
import { inspectionAPI } from '../../lib/api';
import api from '../../lib/api';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Loader2, AlertTriangle, CheckCircle, Camera, LogIn, LogOut, Brain, HelpCircle } from 'lucide-react';

const PHOTO_ORDER = ['front', 'back', 'left', 'right', 'interior_front', 'interior_back'];

const LABELS = {
    front: { en: 'Front', el: 'Μπροστά' },
    back: { en: 'Rear', el: 'Πίσω' },
    left: { en: 'Left Side', el: 'Αριστερά' },
    right: { en: 'Right Side', el: 'Δεξιά' },
    interior_front: { en: 'Interior Front', el: 'Εσωτερικό Μπροστά' },
    interior_back: { en: 'Interior Back', el: 'Εσωτερικό Πίσω' }
};

const LEGACY = { interior_front: 'dashboard', interior_back: 'cargo' };

const AuthedPhoto = ({ url, alt, testId }) => {
    const [src, setSrc] = useState(null);
    const [state, setState] = useState('loading');

    useEffect(() => {
        let objectUrl;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(url, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('fleet_token')}` }
                });
                if (!res.ok) throw new Error('missing');
                const blob = await res.blob();
                objectUrl = URL.createObjectURL(blob);
                if (!cancelled) { setSrc(objectUrl); setState('ready'); }
            } catch {
                if (!cancelled) setState('empty');
            }
        })();
        return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [url]);

    return (
        <div className="aspect-video bg-slate-100 rounded-sm overflow-hidden relative" data-testid={testId}>
            {state === 'loading' && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
                </div>
            )}
            {state === 'ready' && (
                <img
                    src={src}
                    alt={alt}
                    className="w-full h-full object-cover cursor-zoom-in hover:opacity-90 transition-opacity"
                    onClick={() => window.open(src, '_blank')}
                />
            )}
            {state === 'empty' && (
                <div className="absolute inset-0 flex items-center justify-center text-slate-400">
                    <Camera className="w-5 h-5" />
                </div>
            )}
        </div>
    );
};

export const LatestDriverPhotos = ({ inspection }) => {
    const { language } = useLanguage();
    const el = language === 'el';
    const [comparisons, setComparisons] = useState([]);
    const [analyzing, setAnalyzing] = useState(false);

    const loadComparisons = useCallback(async () => {
        if (!inspection?.id) return [];
        try {
            const res = await inspectionAPI.getComparisons({ inspection_id: inspection.id });
            setComparisons(res.data || []);
            return res.data || [];
        } catch {
            return [];
        }
    }, [inspection?.id]);

    useEffect(() => {
        loadComparisons();
    }, [loadComparisons]);

    const runAnalysis = async () => {
        setAnalyzing(true);
        try {
            const res = await api.post(`/photo-comparison/compare-now/${inspection.id}`);
            await loadComparisons();
            toast.success(res.data?.message || (el ? 'Ο έλεγχος ολοκληρώθηκε' : 'Comparison completed'));
        } catch (error) {
            toast.error(error.response?.data?.detail || (el ? 'Ο έλεγχος απέτυχε' : 'Comparison failed'));
        } finally {
            setAnalyzing(false);
        }
    };

    if (!inspection) return null;

    const hasPhoto = (type, list, key) => {
        const accepted = [type, LEGACY[type]].filter(Boolean);
        return (list || []).some(p => accepted.includes(p[key]));
    };

    const resultFor = (type) => comparisons.find(
        c => [type, LEGACY[type]].includes(c.photo_type)
            && (c.comparison_type === 'checkin_vs_checkout' || c.comparison_type === 'manual_checkin_vs_checkout')
    );

    const checkoutCount = (inspection.checkout_photos || []).length;

    return (
        <div className="fleet-card p-6" data-testid="latest-driver-photos">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-6">
                <div>
                    <h2 className="text-base md:text-lg font-semibold text-slate-800">
                        {el ? 'Τελευταίες φωτογραφίες οδηγού' : 'Latest driver photos'}
                    </h2>
                    <p className="text-sm text-zinc-500">
                        {inspection.driver_name} · {new Date(inspection.date).toLocaleDateString(el ? 'el-GR' : 'en-US')}
                        {' · '}
                        {el ? 'Παραλαβή' : 'Check-in'} {(inspection.photos || []).length}/6
                        {' · '}
                        {el ? 'Παράδοση' : 'Check-out'} {checkoutCount}/6
                    </p>
                </div>
                {checkoutCount === 0 && (
                    <span className="text-xs px-2 py-1 rounded-sm bg-amber-50 text-amber-600 self-start">
                        {el ? 'Το check-out δεν έχει γίνει ακόμη' : 'Check-out not completed yet'}
                    </span>
                )}
                {checkoutCount > 0 && (
                    <Button
                        onClick={runAnalysis}
                        disabled={analyzing}
                        className="bg-[#1B7EB5] hover:bg-[#58A6B4] text-white rounded-sm transition-colors self-start"
                        data-testid="run-ai-comparison-btn"
                    >
                        {analyzing
                            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{el ? 'Έλεγχος σε εξέλιξη...' : 'Analyzing...'}</>
                            : <><Brain className="w-4 h-4 mr-2" />{el ? 'Έλεγχος με AI τώρα' : 'Run AI check now'}</>}
                    </Button>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {PHOTO_ORDER.map((type) => {
                    const result = resultFor(type);
                    const hasIn = hasPhoto(type, inspection.photos, 'photo_type');
                    const hasOut = hasPhoto(type, inspection.checkout_photos, 'type');
                    return (
                        <div key={type} className="space-y-2" data-testid={`latest-photo-pair-${type}`}>
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-slate-700">{el ? LABELS[type].el : LABELS[type].en}</span>
                                {result ? (
                                    <span
                                        className={`text-xs px-2 py-0.5 rounded-sm flex items-center gap-1 ${
                                            result.status === 'failed'
                                                ? 'bg-slate-100 text-slate-500'
                                                : result.status === 'invalid_photos'
                                                    ? 'bg-amber-50 text-amber-600'
                                                    : result.damage_detected ? 'bg-red-50 text-red-500' : 'bg-green-50 text-green-600'
                                        }`}
                                        data-testid={`latest-photo-ai-${type}`}
                                    >
                                        {result.status === 'failed'
                                            ? <><AlertTriangle className="w-3 h-3" />{el ? 'Απέτυχε' : 'Failed'}</>
                                            : result.status === 'invalid_photos'
                                                ? <><HelpCircle className="w-3 h-3" />{el ? 'Μη έγκυρες φωτό' : 'Invalid photos'}</>
                                                : result.damage_detected
                                                    ? <><AlertTriangle className="w-3 h-3" />{el ? 'Ζημιά' : 'Damage'}</>
                                                    : <><CheckCircle className="w-3 h-3" />{el ? 'Καθαρό' : 'Clean'}</>}
                                    </span>
                                ) : hasIn && hasOut && (
                                    <span className="text-xs px-2 py-0.5 rounded-sm bg-slate-100 text-slate-500">
                                        {el ? 'Χωρίς έλεγχο' : 'Not checked'}
                                    </span>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                    <span className="text-xs text-zinc-500 flex items-center gap-1">
                                        <LogIn className="w-3 h-3" />{el ? 'Παραλαβή' : 'Check-in'}
                                    </span>
                                    {hasIn
                                        ? <AuthedPhoto url={inspectionAPI.getPhotoUrl(inspection.id, type)} alt={type} testId={`checkin-photo-${type}`} />
                                        : <div className="aspect-video bg-slate-100 rounded-sm flex items-center justify-center text-xs text-slate-400">{el ? 'Καμία' : 'None'}</div>}
                                </div>
                                <div className="space-y-1">
                                    <span className="text-xs text-zinc-500 flex items-center gap-1">
                                        <LogOut className="w-3 h-3" />{el ? 'Παράδοση' : 'Check-out'}
                                    </span>
                                    {hasOut
                                        ? <AuthedPhoto url={inspectionAPI.getCheckoutPhotoUrl(inspection.id, type)} alt={type} testId={`checkout-photo-${type}`} />
                                        : <div className="aspect-video bg-slate-100 rounded-sm flex items-center justify-center text-xs text-slate-400">{el ? 'Καμία' : 'None'}</div>}
                                </div>
                            </div>
                            {result && (result.damage_detected || result.status === 'invalid_photos') && (
                                <p className={`text-xs ${result.status === 'invalid_photos' ? 'text-amber-600' : 'text-red-500'}`}>
                                    {el ? result.description_el : result.description_en}
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
